import type { FC } from 'react';
import { SimpleGrid, VStack, chakra, useCheckbox, useCheckboxGroup } from '@chakra-ui/react';
import { Text } from '../../../../../ui-components/typography';
import { classNames } from '../../../../../utils/tailwind/classNames';
import styles from './ColorFilterSection.module.scss';

type Color = {
    name: string;
    count: number;
};

type ColorSectionProps = {
    colors: Color[];
    onFilterChange: (type: string, value: string) => void;
    isFilterSelected: (type: string, value: string) => boolean;
};

const CustomColorCheckbox: FC<{
    color: Color;
    getCheckboxProps: (props?: Record<string, unknown>) => Record<string, unknown>;
}> = ({ color, getCheckboxProps }) => {
    const checkboxProps = getCheckboxProps({ value: color.name, isDisabled: color.count === 0 });
    const {
        state,
        getInputProps,
        htmlProps,
        getCheckboxProps: getSingleCheckboxProps,
    } = useCheckbox(checkboxProps);

    return (
        <chakra.label
            {...htmlProps}
            className={classNames(
                'flex flex-col items-center gap-2',
                checkboxProps.isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
            data-testid={`search-filter-color-${color.name}`}
        >
            <input {...getInputProps()} hidden />
            <div
                {...getSingleCheckboxProps()}
                className={classNames(
                    'refinement-color-box',
                    `refinement-color-${color.name.toLowerCase().replace(/\s+/g, '-')}`,
                    'border border-slt-pepper-700 m-1 rounded-full',
                    'hover:outline-slt-pepper-700 hover:outline hover:outline-[1.5px] hover:outline-offset-4',
                    {
                        'outline outline-[1.5px] outline-offset-4 border-slt-pepper-1000 outline-slt-pepper-1000':
                            state.isChecked,
                    },
                )}
            />
            <Text size="small" as="span" className="text-center">
                {color.name} ({color.count})
            </Text>
        </chakra.label>
    );
};

export const ColorFilterSection: FC<ColorSectionProps> = ({
    colors,
    onFilterChange,
    isFilterSelected,
}) => {
    const sortedColors = [...colors].sort((a, b) => {
        if (a.name === 'Multi') return 1;
        if (b.name === 'Multi') return -1;
        return 0;
    });

    const selectedValues = sortedColors
        .filter((c) => isFilterSelected('color', c.name))
        .map((c) => c.name);

    const handleCheckboxGroupChange = (newValues: (string | number)[]) => {
        const changedValue =
            newValues.find((value) => !selectedValues.includes(value as string)) ||
            selectedValues.find((value) => !newValues.includes(value));

        if (typeof changedValue === 'string') {
            onFilterChange('color', changedValue);
        }
    };

    const { getCheckboxProps } = useCheckboxGroup({
        value: selectedValues,
        onChange: handleCheckboxGroupChange,
    });

    return (
        <VStack align="stretch" spacing={2} className={styles.colorFilter}>
            <SimpleGrid
                columns={{ base: 4, sm: 8, md: 4 }}
                rowGap={4}
                columnGap={{ base: 1, md: 6 }}
            >
                {sortedColors.map((color) => (
                    <CustomColorCheckbox
                        key={color.name}
                        color={color}
                        getCheckboxProps={getCheckboxProps}
                    />
                ))}
            </SimpleGrid>
        </VStack>
    );
};
